import type {
  ApiResponse,
  User,
  StockQuote,
  CompanyProfile,
  SearchResult,
  Holding,
  PortfolioSummary,
  Transaction,
  WatchlistItem,
  LeaderboardEntry,
  PortfolioHistoryPoint,
} from '@/types'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

class ApiClient {
  private token: string | null = null

  setToken(token: string | null) {
    this.token = token
    if (token) {
      localStorage.setItem('auth_token', token)
    } else {
      localStorage.removeItem('auth_token')
    }
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('auth_token')
    }
    return this.token
  }

  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const token = this.getToken()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    })

    const data = await res.json() as ApiResponse<T>
    return data
  }

  // Auth
  async register(email: string, username: string, password: string) {
    return this.request<{ token: string; user: User }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, username, password }),
    })
  }

  async login(email: string, password: string) {
    return this.request<{ token: string; user: User }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
  }

  async getMe() {
    return this.request<User>('/auth/me')
  }

  // Stocks
  async searchStocks(query: string) {
    return this.request<SearchResult[]>(`/stocks/search?q=${encodeURIComponent(query)}`)
  }

  async getQuote(symbol: string) {
    return this.request<{ quote: StockQuote; profile: CompanyProfile | null }>(
      `/stocks/quote/${encodeURIComponent(symbol)}`
    )
  }

  async getMultipleQuotes(symbols: string[]) {
    return this.request<Record<string, StockQuote>>(
      `/stocks/quotes?symbols=${symbols.join(',')}`
    )
  }

  // Trading
  async executeTrade(symbol: string, type: 'buy' | 'sell', shares: number) {
    return this.request<{
      transaction: Transaction & { realizedPnl?: number }
      newBalance: number
    }>('/trade', {
      method: 'POST',
      body: JSON.stringify({ symbol, type, shares }),
    })
  }

  // Portfolio
  async getPortfolio() {
    return this.request<{
      user: User
      holdings: Holding[]
      summary: PortfolioSummary
    }>('/portfolio')
  }

  async getPortfolioHistory(days = 30) {
    return this.request<PortfolioHistoryPoint[]>(`/portfolio/history?days=${days}`)
  }

  // Transactions
  async getTransactions(params: {
    page?: number
    limit?: number
    symbol?: string
    type?: 'buy' | 'sell'
  } = {}) {
    const query = new URLSearchParams()
    if (params.page) query.set('page', String(params.page))
    if (params.limit) query.set('limit', String(params.limit))
    if (params.symbol) query.set('symbol', params.symbol)
    if (params.type) query.set('type', params.type)

    return this.request<Transaction[]>(`/transactions?${query.toString()}`)
  }

  // Watchlist
  async getWatchlist() {
    return this.request<WatchlistItem[]>('/watchlist')
  }

  async addToWatchlist(symbol: string) {
    return this.request<WatchlistItem>('/watchlist', {
      method: 'POST',
      body: JSON.stringify({ symbol }),
    })
  }

  async removeFromWatchlist(symbol: string) {
    return this.request<{ removed: string }>(`/watchlist/${encodeURIComponent(symbol)}`, {
      method: 'DELETE',
    })
  }

  // Leaderboard
  async getLeaderboard(page = 1, limit = 25) {
    return this.request<LeaderboardEntry[]>(`/leaderboard?page=${page}&limit=${limit}`)
  }
}

export const api = new ApiClient()
